import React, { useRef } from 'react';
import { UploadCloudIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCity } from '../../contexts/CityContext';
import { useEmailIdentity } from '../../contexts/EmailIdentityContext';
import { EmailTemplate } from './EmailTemplate';
const HEX = /^#[0-9a-fA-F]{6}$/;

/** Configurer → Identité: email header and footer, with a live preview. */
export function EmailIdentityConfig() {
  const {
    t,
    lang
  } = useLanguage();
  const {
    city
  } = useCity();
  const {
    identity,
    updateIdentity
  } = useEmailIdentity();
  const fileRef = useRef<HTMLInputElement>(null);
  const onLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) updateIdentity({
      logoUrl: URL.createObjectURL(file)
    });
    e.target.value = '';
  };
  const label = 'block text-sm font-semibold text-slate-900 mb-3';
  const input = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]';
  return <div className="space-y-6" data-id="element-4068" data-mp="3icd3ukm472k9">
      <div data-id="element-4069" data-mp="3dfwh9uukcw9a">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-4070" data-mp="3dg4tdkiv3xjf">{t('Identité graphique', 'Visual identity')}</h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-4071" data-mp="3s37r9sb3k87a">
          {t('En-tête et pied de page appliqués à tous les courriels envoyés aux citoyens. Les modifications s’appliquent immédiatement à l’aperçu des avis.', 'Header and footer applied to every email sent to citizens. Changes apply right away to the notice preview.')}
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-6 items-start" data-id="element-4072" data-mp="39eaxrqnixjq4">
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-8 shadow-sm" data-id="element-4073" data-mp="34e5d4ufo8xhj">
          {/* Header */}
          <section className="space-y-5" aria-labelledby="identity-header" data-id="element-4074" data-mp="omhi54k7xf1d">
            <h4 id="identity-header" className="text-xs font-bold text-slate-400 uppercase tracking-wide" data-id="element-4075" data-mp="joz5p46zshom">
              {t('En-tête', 'Header')}
            </h4>

            <div data-id="element-4076" data-mp="qtqi9hgk0eub">
              <span className={label} data-id="element-4077" data-mp="vqqnxr9hrfkw">{t('Logo officiel', 'Official logo')}</span>
              <div className="flex items-center gap-4 flex-wrap" data-id="element-4078" data-mp="vstmx0smgj1r">
                <button type="button" onClick={() => fileRef.current?.click()} className="w-36 h-20 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center bg-slate-50 text-slate-400 hover:border-[#4F46E5] hover:bg-indigo-50 transition-colors overflow-hidden" data-id="element-4079" data-mp="10r4z3i6e35n5">
                  {identity.logoUrl ? <img src={identity.logoUrl} alt={t('Logo actuel', 'Current logo')} className="max-h-14 max-w-[85%] object-contain" data-id="element-4080" data-mp="hxr8tqdmfndv" /> : <>
                      <UploadCloudIcon className="w-5 h-5 mb-1" aria-hidden="true" data-id="element-4081" data-mp="3lf9je5ks38hz" />
                      <span className="text-xs font-medium" data-id="element-4082" data-mp="3qe3zv0v9wkyx">{t('Téléverser', 'Upload')}</span>
                    </>}
                </button>
                <input ref={fileRef} type="file" accept="image/png,image/svg+xml,image/jpeg" className="hidden" onChange={onLogo} data-id="element-4083" data-mp="23ifvwyhh93zo" />
                <div className="flex-1 min-w-[12rem] text-xs text-slate-500 space-y-1" data-id="element-4084" data-mp="1yijqqvx1n6ij">
                  <p data-id="element-4085" data-mp="1tkrqnem8we39">
                    {t('Sans logo, le nom de la ville s’affiche en titre.', 'Without a logo, the city name is shown as a wordmark.')}
                  </p>
                  <p className="text-slate-400" data-id="element-4086" data-mp="1jt5gzmpk2n2m">{t('PNG transparent ou SVG, 400×100 px max.', 'Transparent PNG or SVG, 400×100 px max.')}</p>
                  {identity.logoUrl && <button type="button" onClick={() => updateIdentity({
                  logoUrl: null
                })} className="inline-flex items-center gap-1 font-medium text-slate-600 hover:text-red-600" data-id="element-4087" data-mp="1av1t4jr3le7n">
                      <XIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-4088" data-mp="30xalqqzq5ap1" />
                      {t('Retirer le logo', 'Remove logo')}
                    </button>}
                </div>
              </div>
            </div>

            <div data-id="element-4089" data-mp="2r7hicd9qk1vv">
              <span className={label} data-id="element-4090" data-mp="2r5y2uovwluv5">{t('Style de l’en-tête', 'Header style')}</span>
              <div className="inline-flex p-1 bg-slate-100 rounded-lg" role="radiogroup" data-id="element-4091" data-mp="2w5aqhbuln5ys">
                {([['brand', t('Bandeau couleur', 'Colour band')], ['light', t('Fond blanc', 'White')]] as const).map(([value, text]) => <button key={value} type="button" role="radio" aria-checked={identity.headerStyle === value} onClick={() => updateIdentity({
                headerStyle: value
              })} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${identity.headerStyle === value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4092" data-mp="2d89jjlhhivs7">
                    {text}
                  </button>)}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4093" data-mp="20x8ex4evo36o">
              <ColorField label={t('Couleur principale', 'Primary colour')} hint={t('Bandeau, liens et icônes', 'Band, links and icons')} value={identity.primaryColor} onChange={v => updateIdentity({
              primaryColor: v
            })} data-id="element-4094" data-mp="2eovog5xtz87e" />
              <ColorField label={t('Couleur d’accent', 'Accent colour')} hint={t('Liseré sous l’en-tête', 'Strip under the header')} value={identity.accentColor} onChange={v => updateIdentity({
              accentColor: v
            })} data-id="element-4095" data-mp="2jnzyeyskx9ys" />
            </div>

            <div data-id="element-4096" data-mp="2pdhwbuldab8z">
              <label htmlFor="identity-slogan" className={label} data-id="element-4097" data-mp="2pe1ffemr75ol">
                {t('Signature de la ville', 'City signature')}
              </label>
              <input id="identity-slogan" type="text" maxLength={60} value={identity.slogan[lang]} onChange={e => updateIdentity({
              slogan: {
                ...identity.slogan,
                [lang]: e.target.value
              }
            })} placeholder={t('Facultatif', 'Optional')} className={input} data-id="element-4098" data-mp="2ydo91e98r773" />
              <p className="text-xs text-slate-500 mt-1.5" data-id="element-4099" data-mp="3d8cbejjchpih">{t('Affichée sous le logo.', 'Shown under the logo.')}</p>
            </div>

            <div data-id="element-4100" data-mp="3m3ijjp638jo6">
              <label htmlFor="identity-tagline" className={label} data-id="element-4101" data-mp="3m1xq62vif6rw">
                {t('Mention de l’en-tête', 'Header label')}
              </label>
              <input id="identity-tagline" type="text" maxLength={30} value={identity.headerTagline[lang]} onChange={e => updateIdentity({
              headerTagline: {
                ...identity.headerTagline,
                [lang]: e.target.value
              }
            })} placeholder={t('Avis aux citoyens', 'Citizen notice')} className={input} data-id="element-4102" data-mp="3r0900dlt1ha4" />
              <p className="text-xs text-slate-500 mt-1.5" data-id="element-4103" data-mp="3s5lt9t0ws0uo">
                {t('Affichée à droite du logo. Changez la langue en haut de page pour saisir la version anglaise.', 'Shown right of the logo. Switch the language at the top of the page to enter the French version.')}
              </p>
            </div>
          </section>

          <hr className="border-slate-100" data-id="element-4104" data-mp="eyshseh0qhdr" />

          {/* Footer */}
          <section className="space-y-5" aria-labelledby="identity-footer" data-id="element-4105" data-mp="eyk44kd2n81i">
            <h4 id="identity-footer" className="text-xs font-bold text-slate-400 uppercase tracking-wide" data-id="element-4106" data-mp="1bhi1tvad8bqf">
              {t('Pied de page', 'Footer')}
            </h4>
            <div data-id="element-4107" data-mp="1qdh1wjp7k3t8">
              <span className={label} data-id="element-4108" data-mp="1qcz95x1r87q2">{t('Style du pied de page', 'Footer style')}</span>
              <div className="inline-flex p-1 bg-slate-100 rounded-lg" role="radiogroup" data-id="element-4109" data-mp="1v4z398v586eq">
                {([['dark', t('Foncé', 'Dark')], ['light', t('Clair', 'Light')]] as const).map(([value, text]) => <button key={value} type="button" role="radio" aria-checked={identity.footerStyle === value} onClick={() => updateIdentity({
                footerStyle: value
              })} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${identity.footerStyle === value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4110" data-mp="2948ju9n9h2j8">
                    {text}
                  </button>)}
              </div>
            </div>
            <div data-id="element-4111" data-mp="3704554ntdh2i">
              <label htmlFor="identity-footer-text" className={label} data-id="element-4112" data-mp="31ywxr6pl2jwd">
                {t('Coordonnées', 'Contact details')}
              </label>
              <textarea id="identity-footer-text" rows={5} value={identity.footerText} onChange={e => updateIdentity({
              footerText: e.target.value
            })} className={`${input} px-4 py-3 resize-none`} data-id="element-4113" data-mp="3fxegeq0mt67f" />
              <p className="text-xs text-slate-500 mt-1.5" data-id="element-4114" data-mp="2j9hv57u91mpv">
                {t('La première ligne s’affiche en gras.', 'The first line is shown in bold.')}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4115" data-mp="2s8v501macdb4">
              <div data-id="element-4116" data-mp="24jj03dxzqrem">
                <label htmlFor="identity-email" className={label} data-id="element-4117" data-mp="24j0ys2wh1mzu">
                  {t('Courriel de contact', 'Contact email')}
                </label>
                <input id="identity-email" type="email" value={identity.email} onChange={e => updateIdentity({
                email: e.target.value
              })} className={input} data-id="element-4118" data-mp="20fuu2zp0c3vh" />
              </div>
              <div data-id="element-4119" data-mp="3ee9139m94lc1">
                <label htmlFor="identity-website" className={label} data-id="element-4120" data-mp="3ehn1icbcukgu">
                  {t('Site web', 'Website')}
                </label>
                <input id="identity-website" type="text" value={identity.website} onChange={e => updateIdentity({
                website: e.target.value
              })} className={input} data-id="element-4121" data-mp="34i5tvpcjlwi8" />
              </div>
            </div>
            <div className="space-y-3" data-id="element-4122" data-mp="2qr0veqkwva9h">
              <Toggle checked={identity.showSocial} onChange={v => updateIdentity({
              showSocial: v
            })} label={t('Afficher les réseaux sociaux', 'Show social media')} data-id="element-4123" data-mp="2qs2e9h86511r" />
              <Toggle checked={identity.showUnsubscribe} onChange={v => updateIdentity({
              showUnsubscribe: v
            })} label={t('Afficher les liens de désabonnement', 'Show unsubscribe links')} data-id="element-4124" data-mp="wa9gzotg2jfx" />
            </div>
          </section>
        </div>

        {/* Live preview */}
        <div className="xl:sticky xl:top-8 space-y-2" data-id="element-4125" data-mp="1a8qz970kz3qg">
          <p className="text-sm font-semibold text-slate-900" data-id="element-4126" data-mp="djhzfzzazmqg">{t('Aperçu', 'Preview')}</p>
          <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-4127" data-mp="dhoqge17wqfa">
            <EmailTemplate identity={identity} cityName={city} title={t('Titre de l’avis', 'Notice title')} bodyHtml={null} placeholder={t('Le contenu de chaque avis s’insère ici, entre l’en-tête et le pied de page.', 'Each notice’s content goes here, between the header and the footer.')} lang={lang} data-id="element-4128" data-mp="igi6oe7w2vj5" />
          </div>
        </div>
      </div>
    </div>;
}
function ColorField({
  label,
  hint,
  value,
  onChange
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [draft, setDraft] = React.useState(value);
  React.useEffect(() => setDraft(value), [value]);
  const invalid = !HEX.test(draft);
  return <div data-id="element-4129" data-mp="16ycaiytts01w">
      <span className="block text-xs text-slate-500 mb-1.5" data-id="element-4130" data-mp="16ylrxy0t0yvz">{label}</span>
      <div className="flex items-center gap-3" data-id="element-4131" data-mp="j76sqe4bqxoy">
        <input type="color" value={value} onChange={e => onChange(e.target.value)} aria-label={label} className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer bg-white p-0.5" data-id="element-4132" data-mp="e7u0teylj0fi" />
        <input type="text" value={draft} aria-label={`${label} (hex)`} aria-invalid={invalid} onChange={e => {
        setDraft(e.target.value);
        if (HEX.test(e.target.value)) onChange(e.target.value);
      }} className={`flex-1 min-w-0 border rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none ${invalid ? 'border-red-300 focus:border-red-500' : 'border-slate-200 focus:border-[#4F46E5]'}`} data-id="element-4133" data-mp="o4ghdrjw35bq" />
      </div>
      <p className="text-[11px] text-slate-400 mt-1" data-id="element-4134" data-mp="225cyy3f9apvx">{hint}</p>
    </div>;
}
function Toggle({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return <label className="flex items-center justify-between gap-4 cursor-pointer" data-id="element-4135" data-mp="2utqu8beyn83s">
      <span className="text-sm text-slate-700" data-id="element-4136" data-mp="2uvuw6ntatx1f">{label}</span>
      <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${checked ? 'bg-[#4F46E5]' : 'bg-slate-300'}`} data-id="element-4137" data-mp="2g4cln5j7kupg">
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : ''}`} data-id="element-4138" data-mp="3dl1kcqcv3gum" />
      </button>
    </label>;
}